import { Component, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { Subscription } from "rxjs";
import { MessagingService } from 'src/app/notifications/services/messages.services';
import { AuthService } from 'src/app/core/services/authentication.service';
import { Utils } from 'src/app/core/resources/utils';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { SwUpdate } from '@angular/service-worker';
import { DialogComponent } from 'src/app/shared/dialog/dialog.component';
import { DynamicFirebaseService } from 'src/app/core/services/dynamic-firebase.service';
import { GeneralConfigService } from 'src/app/core/services/general-config.service';
import { NotificationHealthService } from 'src/app/notifications/services/notification-health.service';
import { LastNotificationListenerService } from 'src/app/core/services/last-notification-listener.service';
import { CargoSearchAccessService } from 'src/app/modules/cargo/cargo-search/cargo-search-access.service';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MainComponent {
  navCollapse = false;
  navHover: boolean = false;
  termsAndConditionsActive: boolean = false;
  hasGpsAlert: boolean = false;
  rndcContingency: boolean = false;
  isOnCargoSearchPage = false;
  private rndcContingencySub: Subscription;

  constructor(
    private messagingService: MessagingService,
    public authService: AuthService,
    public utils: Utils,
    private router: Router,
    private titleService: Title,
    private activatedRoute: ActivatedRoute,
    private matDialog: MatDialog,
    public dynamicService: DynamicFirebaseService,
    private generalConfigService: GeneralConfigService,
    private notificationHealthService: NotificationHealthService,
    private lastNotificationListenerService: LastNotificationListenerService,
    private cargoSearchAccessService: CargoSearchAccessService,
    updates: SwUpdate,
    private cdr: ChangeDetectorRef
  ) {
    if (authService.sessionActive()) {
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
      ).subscribe(() => {
        const title = this._buildBreadcrumbs(this.activatedRoute.root).map((title) => {
          return title.label;
        }).join(" / ");;
        this.titleService.setTitle(title.toString());
        try {
          switch (this.activatedRoute.firstChild.routeConfig.path) {
            case 'home':
              this.navCollapse = false;
              break;
            default:
              this.navCollapse = true;
              break;
          }
        } catch (e) {
          this.navCollapse = false;
        }
      });
    }
  }

  private _buildBreadcrumbs(
    activatedRoute: ActivatedRoute,
    url: string = ""
  ) {
    const children: ActivatedRoute[] = activatedRoute.children;

    if (children.length === 0) {
      return [];
    }

    let breadcrumbs = [];

    for (const child of children) {
      const routeURL: string = child.snapshot.url
        .map(segment => segment.path)
        .join("/");

      if (routeURL !== "") {
        url += `/${routeURL}`;
      }

      const routeData = child.snapshot.data;

      if (routeData.breadcrumb) {
        breadcrumbs.push({ label: routeData.breadcrumb, url: url });
      } else if (routeData.apiData && routeData.apiData.breadcrumb) {
        breadcrumbs.push({
          label: routeData.apiData.breadcrumb,
          url: url
        });
      }

      breadcrumbs = breadcrumbs.concat(this._buildBreadcrumbs(child, url));
    }

    return breadcrumbs;
  }

  getChildTitle(activatedRoute: ActivatedRoute) {
    if (activatedRoute.firstChild) {
      return this.getChildTitle(activatedRoute.firstChild);
    } else {
      return activatedRoute;
    }
  }

  // tslint:disable-next-line: use-lifecycle-interface
  async ngOnInit() {
    this.messagingService.listenForegroundMessagesInConsole();
    this.updateCargoSearchPageState(this.router.url);
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
    ).subscribe((event: NavigationEnd) => {
      this.updateCargoSearchPageState(event.urlAfterRedirects);
    });

    if (this.authService && this.authService.sessionActive()) {
      this.generalConfigService.startRndcContingencyListener();
      this.rndcContingencySub = this.generalConfigService.rndcContingency$.subscribe((value: boolean) => this.rndcContingency = value);
      await this.dynamicService.initializeAllFirebaseApps();
      if (this.authService.getUserSession().lastUserTerm && this.authService.getUserSession().lastUserTerm.state) {
        this.termsAndConditionsActive = true;
        this.lastNotificationListenerService.start();
        this.messagingService.registerCurrentUserFcmSubscription(false);
        this.notificationHealthService.start();

        // Dialog if session is inactivated
        const user = this.authService.getUserSession();
        if (user.role === 'webUserPendingActivate' || user.roleId === null) {
          const dialogConfig = new MatDialogConfig();
          dialogConfig.data = {
            title: 'Su usuario se encuentra pendiente por activar.',
            description: 'Comuníquese con su administrador.',
            hideBtnCancel: true
          };
          dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
          dialogConfig.width = ModalEnum.EXTRA_SMALL_WIDTH;
          dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
          dialogConfig.autoFocus = false;
          this.matDialog.open(DialogComponent, dialogConfig)
        }
      } else {
        this.termsAndConditionsActive = false;
        this.router.navigate(['/account/validate-terms']);
      }
    }
  }

  // tslint:disable-next-line: use-lifecycle-interface
  async ngOnDestroy() {
    this.lastNotificationListenerService.stop();
    this.notificationHealthService.stop();
    this.messagingService.stopForegroundMessagesInConsole();
    this.messagingService.stopCurrentUserFcmSubscriptionRegistration();

    if (this.authService && this.authService.sessionActive()) {
      await this.authService.updateUId(null, true).toPromise();
    }
    if (this.rndcContingencySub) this.rndcContingencySub.unsubscribe();
    this.generalConfigService.stopRndcContingencyListener();
  }

  get isOutOfCargoCreate() {
    return window.location.hash !== "#/cargo/create";
  }

  get showSearchFab(): boolean {
    return this.authService.sessionActive()
      && !this.isOnCargoSearchPage
      && this.cargoSearchAccessService.showSearchShortcut;
  }

  goToCargoSearch(): void {
    this.router.navigate(['/cargo/search']);
  }

  private updateCargoSearchPageState(url: string): void {
    this.isOnCargoSearchPage = url.includes('/cargo/search');
  }

  goToWA() {
    window.open('https://wa.me/573173678485?text=¡Hola! Quisiera obtener más información.');
  }

  mouseEvent($event: MouseEvent) {
    if ($event.type === 'mouseleave') {
      this.navHover = false;
    }
    if ($event.type === 'mouseenter') {
      this.navHover = true;
    }
  }

  onGpsAlertVisibilityChange(isVisible: boolean) {
    setTimeout(() => {
      this.hasGpsAlert = isVisible;
      this.cdr.detectChanges();
    }, 0);
  }

}
